import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { usePackaging } from '../../hooks/usePackaging';
import { PackagingFilters, PackagingTabKey } from '../../models/Packaging';
import PackagingGrid from './PackagingGrid';
import PackagingHeader from './PackagingHeader';
import PackagingModals, { PackagingModalsRef } from './PackagingModals';
import PackagingStates from './PackagingStates';
import { PackagingTabs } from '../interaction';
import { useDeletePackaging } from '../../hooks/useDeletePackaging';
import { PackagingItem } from '../../models/Packaging';

const Main: React.FC = () => {
  const { t } = useTranslation('packagingModule');
  const modalsRef = useRef<PackagingModalsRef>(null);
  const confirmDeleteRef = useRef<ConfirmModalRef>(null);
  const { deletePackaging } = useDeletePackaging();

  const { params } = useQueryParams({
    search: { type: 'string', default: '' },
  });

  // Estado local para filtros
  const searchTerm = params.search;

  // Estado para la pestaña activa
  const [activeTab, setActiveTab] = useState<PackagingTabKey>('all');

  // Query "Todos"
  const allFilters = useMemo<PackagingFilters>(() => {
    return {
      limit: 20,
      searchTerm: searchTerm ? searchTerm.trim() : undefined,
      categories: ['customer_box', 'marketing_item'],
    };
  }, [searchTerm]);

  // Query "Marketing"
  const marketingFilters = useMemo(() => {
    return {
      limit: 20,
      searchTerm: searchTerm ? searchTerm.trim() : undefined,
      categories: ['marketing_item'] as const,
    };
  }, [searchTerm]);

  const allQuery = usePackaging(allFilters);
  const marketingQuery = usePackaging(marketingFilters);

  const boxesFilters = useMemo(() => {
    return {
      limit: 20,
      searchTerm: searchTerm ? searchTerm.trim() : undefined,
      categories: ['customer_box'] as const,
    };
  }, [searchTerm]);

  const boxesQuery = usePackaging(boxesFilters);

  // Datos por tab:
  // - all: usa query "Todos"
  // - marketing: usa query "Marketing"
  // - boxes: usa query "Cajas" filtrada solo por customer_box
  const items = useMemo(() => {
    if (activeTab === 'marketing') {
      return marketingQuery.items;
    }
    if (activeTab === 'boxes') {
      return boxesQuery.items;
    }
    return allQuery.items;
  }, [activeTab, allQuery.items, marketingQuery.items, boxesQuery.items]);

  const activeQuery = useMemo(() => {
    if (activeTab === 'marketing') return marketingQuery;
    if (activeTab === 'boxes') return boxesQuery;
    return allQuery;
  }, [activeTab, allQuery, boxesQuery, marketingQuery]);

  const activeError = activeQuery.error;
  const activeIsLoading = activeQuery.isLoading;
  const activeHasNextPage = activeQuery.hasNextPage;
  const activeIsFetchingNextPage = activeQuery.isFetchingNextPage;
  const activeFetchNextPage = activeQuery.fetchNextPage;

  // Hook para infinite scroll
  const { lastElementRef } = useInfiniteScroll({
    hasNextPage: activeHasNextPage ?? false,
    isFetchingNextPage: activeIsFetchingNextPage,
    fetchNextPage: activeFetchNextPage,
    enabled: !activeIsLoading && !activeError,
  });

  // Handler para crear material
  const handleCreatePackaging = useCallback(() => {
    modalsRef.current?.openCreateModal();
  }, []);

  const handleEditPackaging = useCallback((item: PackagingItem) => {
    modalsRef.current?.openEditModal(item);
  }, []);

  const handleAdjustStock = useCallback((item: PackagingItem) => {
    modalsRef.current?.openAdjustStockModal(item);
  }, []);

  const handleDeletePackaging = useCallback(
    (item: PackagingItem) => {
      confirmDeleteRef.current?.open(
        () => {
          // Endpoint pendiente en backend: mantenemos el intento para dejar el flujo listo
          deletePackaging(item.id);
        },
        undefined,
        t('modal.delete.confirm_title'),
        t('modal.delete.confirm_message', { name: item.name }),
      );
    },
    [deletePackaging, t],
  );

  // Breadcrumbs y acciones del header
  const breadcrumbs = useMemo(() => [{ label: 'Packaging', path: '/packaging' }], []);

  const headerActions = useMemo(() => {
    const actions = [];

    actions.push({
      id: 'create',
      label: t('actions.create_material'),
      onClick: handleCreatePackaging,
      variant: 'primary' as const,
    });

    return actions;
  }, [handleCreatePackaging, t]);

  usePageNavigation({ breadcrumbs, headerActions });

  // Handler para cambio de pestaña
  const handleTabChange = useCallback((tab: PackagingTabKey) => {
    setActiveTab(tab);
  }, []);

  // Handler para refetch
  const handleRefetch = useCallback(() => {
    allQuery.refetch();
    marketingQuery.refetch();
    boxesQuery.refetch();
  }, [allQuery, marketingQuery, boxesQuery]);

  // Totales por pestaña:
  // - all: viene de meta.total de la query "Todos"
  // - marketing: viene de meta.total de la query "Marketing"
  // - boxes: viene de meta.total de la query filtrada por customer_box
  const tabCounts = useMemo(() => {
    return {
      all: allQuery.total,
      boxes: boxesQuery.total,
      marketing: marketingQuery.total,
    } as Record<PackagingTabKey, number>;
  }, [allQuery.total, marketingQuery.total, boxesQuery.total]);

  return (
    <div className="px-6 my-4">
      {/* Pestañas de filtrado: Todos / Cajas / Marketing */}
      <PackagingTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        counts={tabCounts}
        isLoadingCounts={allQuery.isLoading || marketingQuery.isLoading || boxesQuery.isLoading}
      />

      {/* Header */}
      <PackagingHeader />

      {/* Estados - Loading, Error, Empty */}
      <PackagingStates
        isLoading={activeIsLoading}
        error={activeError}
        itemsCount={items.length}
        searchTerm={searchTerm}
        onRefetch={handleRefetch}
        onCreatePackaging={handleCreatePackaging}
      />

      {/* Grid - Materiales con infinite scroll */}
      <PackagingGrid
        items={items}
        error={activeError}
        isFetchingNextPage={activeIsFetchingNextPage}
        lastElementRef={lastElementRef}
        onEdit={handleEditPackaging}
        onDelete={handleDeletePackaging}
        onAdjustStock={handleAdjustStock}
      />

      {/* Modales - Gestión centralizada */}
      <PackagingModals ref={modalsRef} onRefetch={handleRefetch} />
      <ConfirmModal ref={confirmDeleteRef} />
    </div>
  );
};

export default Main;
